import { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Client-side shopping basket. Persists to localStorage so items survive
 * across sessions (per user's request). The basket only stores IDs +
 * quantities — the frontend re-fetches product details from the backend so
 * price/name/image stay in sync when the admin edits a product.
 */

const STORAGE_KEY = "slh_basket_v1";
const BasketContext = createContext(null);

function readStore() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    // Backfill billing_interval on carts persisted before multi-interval
    // pricing existed — they were always one-off purchases.
    return Array.isArray(parsed) ? parsed.map((i) => ({ billing_interval: "one_off", ...i })) : [];
  } catch {
    return [];
  }
}

function writeStore(items) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* localStorage full/blocked — silent */ }
}

export function BasketProvider({ children }) {
  const [items, setItems] = useState(readStore);   // [{product_id, quantity: 1, billing_interval}]
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { writeStore(items); }, [items]);

  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  // Each product can only appear once (quantity is always 1). Repeated adds
  // are no-ops so the shopper always has 1-or-0 of each item. To bump the
  // quantity, users would have to add different products.
  //
  // NOTE: the "one billing type at a time" rule (a basket can't mix a
  // one-off item with a subscription, since Stripe Checkout can't combine
  // mode=payment and mode=subscription in one session) is enforced by the
  // CALLER (ProductsModule's handleAdd), not here — that's where live
  // product pricing is available to tell a genuinely free item (exempt
  // from the rule) apart from a paid one. Storing that distinction on the
  // basket item itself would go stale the moment an admin changes a price,
  // or across a shopper's localStorage-persisted session.
  const add = (productId, billingInterval = "one_off") => {
    if (items.some((i) => i.product_id === productId)) return;
    setItems((prev) => prev.some((i) => i.product_id === productId) ? prev : [...prev, { product_id: productId, quantity: 1, billing_interval: billingInterval }]);
  };

  const remove = (productId) =>
    setItems((prev) => prev.filter((i) => i.product_id !== productId));

  const has = (productId) => items.some((i) => i.product_id === productId);

  const clear = () => setItems([]);

  const count = useMemo(() => items.length, [items]);
  const currentInterval = items[0]?.billing_interval || null;

  return (
    <BasketContext.Provider value={{ items, add, remove, has, clear, count, currentInterval, drawerOpen, openDrawer, closeDrawer }}>
      {children}
    </BasketContext.Provider>
  );
}

export function useBasket() {
  const ctx = useContext(BasketContext);
  if (!ctx) {
    // Fallback used while the provider is being wired — behaves as an empty
    // read-only basket instead of throwing so page trees don't blank out.
    return { items: [], add: () => ({ success: true }), remove: () => {}, has: () => false, clear: () => {}, count: 0, currentInterval: null, drawerOpen: false, openDrawer: () => {}, closeDrawer: () => {} };
  }
  return ctx;
}
